import type { BookingState } from "../page";
import { CALLOUT_MINIMUM } from "../page";
import { bookingServices, timeSlots } from "../../../mocks/bookingData";

interface OrderSummaryProps {
  booking: BookingState;
  total: number;
  compact?: boolean;
}

export default function OrderSummary({ booking, total, compact = false }: OrderSummaryProps) {
  const hasItems  = (booking.stepType === "items" || booking.stepType === "bin") && booking.cartItems.length > 0;
  const hasOption = booking.stepType !== "items" && booking.stepType !== "bin" && !!booking.selectedOptionLabel;

  if (!booking.serviceTitle) return null;

  const serviceConfig = bookingServices.find((s) => s.slug === booking.serviceSlug);

  // Items/options subtotal — same logic as page.tsx getTotal()
  const itemsSubtotal =
    booking.stepType === "items" || booking.stepType === "bin"
      ? booking.cartItems.reduce((s, c) => s + c.quantity * c.unitPrice, 0)
      : booking.selectedOptionPrice + booking.cartItems.reduce((s, c) => s + c.quantity * c.unitPrice, 0);

  // Callout only applies when service has one configured AND subtotal is below £99
  const calloutApplies =
    !!serviceConfig?.calloutCharge &&
    serviceConfig.calloutCharge > 0 &&
    itemsSubtotal < CALLOUT_MINIMUM;

  return (
    <div className={`bg-white rounded-2xl overflow-hidden cartoon-card ${compact ? "" : "sticky top-24"}`}>
      <div className="bg-cl-dark px-5 py-4">
        <p className="text-cl-lime text-xs font-black uppercase tracking-widest mb-0.5">Order Summary</p>
        <p className="text-white font-black text-base">{booking.serviceTitle}</p>
      </div>

      {serviceConfig?.image && (
        <div className="w-full h-40 bg-[#f3f4f2] flex items-center justify-center overflow-hidden border-b-2 border-transparent">
          <img src={serviceConfig.image} alt={booking.serviceTitle} className="h-[85%] w-auto object-contain" />
        </div>
      )}

      <div className="p-5 flex flex-col gap-4">

        {/* Collection postcode */}
        {booking.postcode && (
          <div className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-cl-tint border border-transparent flex-shrink-0">
              <i className="ri-map-pin-line text-cl-dark text-xs"></i>
            </span>
            <div>
              <p className="text-xs font-black text-gray-500 uppercase tracking-wide">Collection</p>
              <p className="text-sm font-bold text-cl-dark">{booking.postcode.toUpperCase()}</p>
              {booking.addressLine1 && (
                <p className="text-xs text-gray-500">{booking.addressLine1}{booking.city ? `, ${booking.city}` : ""}</p>
              )}
            </div>
          </div>
        )}

        {/* Items list */}
        {hasItems && (
          <div>
            <p className="text-xs font-black text-gray-500 uppercase tracking-wide mb-2">Items</p>
            <div className="flex flex-col gap-1.5">
              {booking.cartItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-cl-dark">{item.quantity}x {item.label}</span>
                  <span className="text-sm font-black text-cl-dark">£{(item.quantity * item.unitPrice).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Option (load/rooms) */}
        {hasOption && (
          <div className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-cl-tint border border-transparent flex-shrink-0">
              <i className="ri-box-3-line text-cl-dark text-xs"></i>
            </span>
            <div>
              <p className="text-xs font-black text-gray-500 uppercase tracking-wide">Selection</p>
              <p className="text-sm font-bold text-cl-dark">{booking.selectedOptionLabel}</p>
            </div>
          </div>
        )}

        {/* Date & time */}
        {booking.date && (
          <div className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-cl-tint border border-transparent flex-shrink-0">
              <i className="ri-calendar-line text-cl-dark text-xs"></i>
            </span>
            <div>
              <p className="text-xs font-black text-gray-500 uppercase tracking-wide">Date &amp; Time</p>
              <p className="text-sm font-bold text-cl-dark">
                {new Date(booking.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long" })}
              </p>
              {booking.timeSlotLabel && <p className="text-xs text-gray-500">{booking.timeSlotLabel}</p>}
            </div>
          </div>
        )}

        {/* Price breakdown + total */}
        {(hasItems || hasOption || calloutApplies) && (
          <>
            <div className="border-t-2 border-transparent pt-3">

              {/* Items breakdown */}
              {hasItems && (
                <div className="flex flex-col gap-1 mb-2">
                  {booking.cartItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 font-semibold">{item.quantity}x {item.label}</span>
                      <span className="font-black text-cl-dark">£{(item.quantity * item.unitPrice).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Option price */}
              {hasOption && booking.selectedOptionPrice > 0 && (
                <div className="flex items-center justify-between mb-2 text-sm">
                  <span className="text-gray-600 font-semibold">{booking.selectedOptionLabel}</span>
                  <span className="font-black text-cl-dark">£{booking.selectedOptionPrice.toFixed(2)}</span>
                </div>
              )}

              {/* Callout charge — only when subtotal < £99 */}
              {calloutApplies && (
                <div className="flex items-center justify-between mb-2 text-sm">
                  <span className="text-gray-600 font-semibold">Callout charge</span>
                  <span className="font-black text-cl-dark">£{serviceConfig!.calloutCharge!.toFixed(2)}</span>
                </div>
              )}

              {/* Time slot surcharge */}
              {(() => {
                const slot = timeSlots.find((t) => t.id === booking.timeSlot);
                return slot && slot.price > 0 ? (
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span className="text-gray-600 font-semibold">{slot.label} slot</span>
                    <span className="font-black text-orange-500">+£{slot.price.toFixed(2)}</span>
                  </div>
                ) : null;
              })()}

              <div className="border-t border-gray-100 pt-2 mt-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-black text-cl-dark">Total (inc. VAT)</span>
                  <span className="text-xl font-black text-cl-green">£{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-1">
              {[
                { icon: "ri-shield-check-line", text: "Fully licensed carrier" },
                { icon: "ri-file-text-line",    text: "Transfer note included" },
                { icon: "ri-scales-3-line",     text: "Max weight: 1,000 kg" },
                { icon: "ri-archive-line",      text: "Max volume: 18 yd³" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2 text-xs text-gray-500">
                  <i className={`${item.icon} text-cl-green flex-shrink-0`}></i>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}